<template>
  <div class="organisation-details" v-if="model" :class="{ updating: saveInProgress }">
    <OrganisationMemberMoveDialog ref="memberMoveDialog" />
    <AccreditedUserAreaOrganisationGroupAddDialog ref="groupAddDialog" />
    <div class="row">
      <div class="col-sm-12">
        <h1>{{ model.name }}</h1>
        <div class="invite-url-container">
          <button 
            v-if="!inviteUrl"
            class="btn btn-primary" 
            @click="getInviteCode"
            :disabled="isLoadingInvite"
          >
            {{ isLoadingInvite ? 'Loading...' : 'Get Invite Link' }}
          </button>
          <template v-else>
            <input 
              type="text" 
              class="form-control" 
              :value="inviteUrl" 
              readonly 
              ref="inviteUrlInput"
            />
            <button 
              class="btn btn-default" 
              @click="copyToClipboard"
              :disabled="isCopied"
            >
              {{ isCopied ? 'Copied!' : 'Copy to Clipboard' }}
            </button>
          </template>
        </div>
      </div>
    </div>
    <div class="row nav-row">
      <div class="col-sm-8">
        <ul class="nav nav-pills tab-list" role="tablist">
          <li role="presentation" :class="{ active: currentTab === 'members' }">
            <a href="#" role="tab" data-test="members-tab" @click.prevent="selectMembersTab"
              >Members ({{ model.members.length }})</a
            >
          </li>
          <li role="presentation" :class="{ active: currentTab === 'teams' }">
            <a href="#" role="tab" data-test="teams-tab" @click.prevent="currentTab = 'teams'"
              >Teams ({{ model.teams.length }})</a
            >
          </li>
          <li role="presentation" :class="{ active: currentTab === 'analysisGroups' }">
            <a href="#" role="tab" data-test="org-analysis-groups-tab" @click.prevent="currentTab = 'analysisGroups'"
              >Analysis Groups ({{ model.analysisGroups.length }})</a
            >
          </li>
          <li role="presentation" v-if="isSuperAccredited" :class="{ active: currentTab === 'data' }">
            <a href="#" role="tab" data-test="org-data-tab" @click.prevent="currentTab = 'data'"
              >Data</a
            >
          </li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-8 content-column">
        <!-- Tab panes -->

        <MembersTab
          v-show="currentTab === 'members'"
          :model="model"
          @revalidate-org-data="revalidateData"
        />

        <AccreditedUserAreaTeamsTab
          v-show="currentTab === 'teams'"
          :id="id"
          :model="model"
          :teamsOrGroups="TeamsOrGroups"
          :canCreateGroupsAndTeams="true"
          :sortedTeams="sortedTeams"
          @add-groups="addGroups"
        />

        <AccreditedUserAreaAnalysisGroupsTab
          v-show="currentTab === 'analysisGroups'"
          :id="id"
          :model="model"
          :teamsOrGroups="TeamsOrGroups"
          :canCreateGroupsAndTeams="true"
          :sortedAnalysisGroups="sortedAnalysisGroups"
          @add-groups="addGroups"
        />

        <AccreditedUserAreaDataTab
          v-if="isSuperAccredited"
          v-show="currentTab === 'data'"
          :model="model"
          @revalidate-org-data="revalidateData"
        />
      </div>
    </div>
  </div>

  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import axios from "axios";
import moment from "dayjs";
import { debug as d } from "debug";
import { useVuelidate } from "@vuelidate/core";
import { maxLength, required } from "@vuelidate/validators";
import TeamsOrGroups from "../../typings/enums/teams-or-groups";
import { setTitle } from "../../services/utilities/set-title";
import {
  AccreditationStatusApiModel,
  GroupAndMembersApiModel,
  OrganisationAndMembersApiModel,
  OrganisationDescriptionApiModel,
  OrganisationMemberApiModel,
  OrganisationMemberApiRole,
  OrganisationMemberUpdateApiModel,
} from "../../api-models";
import OrganisationMemberMoveDialog from "../../components/admin/organisation/organisation-member-move-dialog.vue";
import AccreditedUserAreaOrganisationGroupAddDialog from "../../components/admin/organisation/accredited-user-area-organisation-group-add-dialog.vue";
import AccreditedUserAreaDataTab from "../../components/admin/organisation/accredited-user-area-data-tab.vue";
import AccreditedUserAreaTeamsTab from "../../components/admin/organisation/accredited-user-area-teams-tab.vue";
import AccreditedUserAreaAnalysisGroupsTab from "../../components/admin/organisation/accredited-user-area-analysis-groups-tab.vue";
import MembersTab from "../../components/admin/organisation/accredited-user-area-members-tab.vue";
import LoadingPage from "../loading-page.vue";
import organisationNameRegex from "../../services/utilities/organisation-name-regex";

const debug = d("cs.AccreditedUserAreaOrganisationDetailPage");

function sortGroups(groups: GroupAndMembersApiModel[]): GroupAndMembersApiModel[] {
  return groups.slice().sort((a, b) => {
    if (a.group.name < b.group.name) {
      return -1;
    }
    if (a.group.name > b.group.name) {
      return 1;
    }

    return 0;
  });
}

const props = defineProps<{
  id: string;
}>();

const model = ref<OrganisationAndMembersApiModel | null>(null);
const visibleOrganisations = ref<OrganisationDescriptionApiModel[]>([]);
const saveInProgress = ref(false);
const appliedFilter = ref<string | null>(null);
const memberMoveDialog = ref();
const groupAddDialog = ref();

const rules = {};

const state = {};

const v$ = useVuelidate(rules, state, { $autoDirty: true });

const sortedTeams = computed(() => {
  return model.value ? sortGroups(model.value.teams) : [];
});

const sortedAnalysisGroups = computed(() => {
  return model.value ? sortGroups(model.value.analysisGroups || []) : [];
});

const isLoadingInvite = ref(false);
const inviteUrl = ref('');
const inviteUrlInput = ref<HTMLInputElement | null>(null);
const isCopied = ref(false);

async function loadModel() {
  debug("loadModel started");
  saveInProgress.value = true;
  visibleOrganisations.value = (await axios.get("/api/accrediteduserarea/organisation")).data;
  model.value = (await axios.get(`/api/accrediteduserarea/organisation/${props.id}`)).data;
  saveInProgress.value = false;
  setTitle(`Organisation: ${props.id}`);
  debug("loadModel finished");
}

// In a real implementation, we would call an API to get the accreditation status
// For now, we'll just set it to true for testing purposes
async function loadAccreditationStatus() {
  try {
    const response = await axios.get(`/api/accrediteduserarea/organisation/accreditation/${props.id}`);
    isSuperAccredited.value = response.data.isSuperAccredited;
  } catch (e) {
    debug("Error loading accreditation status:", e);
    // Default to false if we can't determine the status
    isSuperAccredited.value = false;
  }
}

onMounted(async () => {
  debug("onMounted started");
  if (!props?.id) {
    return;
  }
  await loadModel();
  await loadAccreditationStatus();
  
  // Check for tab parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get('tab');
  if (tabParam && ['members', 'teams', 'analysisGroups', 'data'].includes(tabParam)) {
    currentTab.value = tabParam;
  }
  
  debug("onMounted finished");
});

async function revalidateData() {
  try {
    await loadModel();
  } catch (e) {
    debug(e);
  }
}

function changeRole(member: OrganisationMemberApiModel, newRole: OrganisationMemberApiRole) {
  if (member.role === newRole) {
    return;
  }

  saveInProgress.value = true;
  axios
    .post(
      "/api/accrediteduserarea/organisationmember/",
      {
        userId: member.shortUserDetails.id,
        organisationId: props.id,
        role: newRole,
      } as OrganisationMemberUpdateApiModel,
      {
        headers: { "content-type": "application/json" },
      },
    )
    .then((resp) => {
      saveInProgress.value = false;
      member.role = newRole;
    })
    .catch((error) => {
      alert("There was a problem changing the role, please try again.");
      saveInProgress.value = false;
    });
}

async function addGroups(teamsOrGroups) {
  debug("AddGroups");
  if (groupAddDialog.value) {
    const result = await groupAddDialog.value.show(
      model.value,
      teamsOrGroups,
      `Find ${teamsOrGroups === TeamsOrGroups.Teams ? "Team" : "Analysis Group"}`
    );
    if (result) await loadModel();
  }
}

// TAB SYSTEM
const currentTab = ref("members");

function selectMembersTab() {
  appliedFilter.value = null;
  currentTab.value = "members";
}

const isSuperAccredited = ref(false);

async function getInviteCode() {
  if (!model.value) return;
  
  isLoadingInvite.value = true;
  try {
    const response = await axios.get(`/api/accrediteduserarea/orginvite/${model.value.id}`);
    inviteUrl.value = response.data.inviteUrl;
  } catch (error) {
    console.error('Error getting invite code:', error);
    alert('Failed to get invite code. Please try again.');
  } finally {
    isLoadingInvite.value = false;
  }
}

async function copyToClipboard() {
  if (!inviteUrlInput.value) return;
  
  try {
    await navigator.clipboard.writeText(inviteUrlInput.value.value);
    isCopied.value = true;
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    alert('Failed to copy to clipboard. Please try again.');
  }
}
</script>

<style scoped lang="less">
.organisation-details {
  margin: 0 auto;
  width: 95%;
}

.organisation-details.updating {
  opacity: 0.5;
  cursor: wait;

  & > * {
    user-select: none;
    pointer-events: none;
  }
}

.renaming-container {
  input {
    max-width: 580px;
    width: 580px;
  }
}

.renaming-buttons {
  display: inline-block;
  margin-left: 10px;

  button {
    margin-left: 10px;
  }
}

.tab-list {
  padding: 1rem 0;
  border-bottom: 1px solid lightgray;
}

.tab-pane table td {
  line-height: 2;
}

.dropdown-menu {
  // Position to the left to ensure visibility on mobile
  right: 0;
  left: unset;
}

.invite-url-container {
  display: flex;
  gap: 1rem;
  align-items: center;
  
  input {
    flex: 1;
    max-width: 600px;
  }
}
</style> 